import {
    defineElement,
    FC,
    onDidLoad,
    useElement,
    useElements,
    useListen,
    useProp,
    useRef,
    useWatch,
} from '@atomify/hooks';
import { useClickOutside } from '@bpd-library/utilities';
import { mediaIsMatch, min } from '@source/utilities/media-query';

import { BPDInput } from './input';

const FLOATING_LABEL = 'floating-label-active';
const SELECT_DROPDOWN_ACTIVE = 'select__dropdown--is-active';
const SELECT_ITEM_ACTIVE = 'select__dropdown-item--selected';
const SELECT_ITEM_FOCUS = 'select__dropdown-item--focused';
const DROPDOWN_SCROLL = 42;

const Select: FC = ({ element }) => {
    const selectInput = useElement<BPDInput>('bpd-input');
    const selectElement = useElement<HTMLSelectElement>('select');
    const dropdown = useElement<HTMLSelectElement>('[js-hook-select-dropdown]');
    const selectItems = useElements<HTMLButtonElement[]>('[js-hook-select-item]');
    const [mobileEnabled] = useProp<boolean>('mobileEnabled', false);
    const [disableEvents] = useProp<boolean>('disableEvents', false);
    const [preselected] = useProp<boolean>('preselected', false);

    const dropdownOpen = useRef<boolean>(false);
    const selectedItem = useRef<HTMLButtonElement | null>(null);
    const focusedItem = useRef<HTMLButtonElement | null>(null);
    const form = element.closest('form');
    let timeout: number | undefined;

    // Close the dropdown when clicking outside of the component
    useClickOutside(element, (clickOutside) => {
        if (dropdownOpen.current && clickOutside) {
            closeDropdown();
        }
    });

    useListen(selectInput, 'mousedown', onInputClick);
    useListen(selectItems, 'click', onItemClick);
    useListen(element, 'keydown', onKeyDown);

    // Watch dropdownactive ref
    useWatch(() => {
        toggleDropdown(dropdownOpen.current);
    });

    onDidLoad(() => {
        setTimeout(() => preselected && element.classList.add(FLOATING_LABEL), 100);
    });

    function openDropdown() {
        dropdownOpen.current = true;
        if (selectedItem.current) focusItem(selectedItem.current);
        else if (selectItems.current.length) focusItem(selectItems.current[0]);
    }

    function closeDropdown() {
        dropdownOpen.current = false;
        focusItem(null);
        selectInput.current?.validate();
    }

    function onInputClick(e: MouseEvent) {
        if (mobileEnabled || mediaIsMatch(min('tabletLandscape'))) {
            e.preventDefault();
            !dropdownOpen.current && openDropdown();
        }
    }

    function onItemClick(e: MouseEvent) {
        e.preventDefault();
        const target = e.currentTarget as HTMLButtonElement;
        target && selectItem(target);
        closeDropdown();
    }

    function onKeyDown(e: KeyboardEvent) {
        const hijackEvent = () => {
            e.preventDefault();
            e.stopPropagation();
        };

        const updateItem = (mode: 'focus' | 'select') => {
            const currentValue = selectedItem.current?.dataset.value;
            const newValue = selectElement.current?.value;
            if (!newValue || newValue === currentValue) return;

            const newItem = [...selectItems.current].find(
                (item) => item?.dataset.value === newValue,
            );

            if (!newItem) return;

            if (mode === 'select') {
                selectItem(newItem);
                newItem.click();
            } else {
                if (!dropdownOpen.current) openDropdown();
                focusItem(newItem);
            }
        };

        switch (e.key) {
            case 'Enter': {
                hijackEvent();
                if (!dropdownOpen.current) {
                    openDropdown();
                } else {
                    if (focusedItem.current && focusedItem.current !== selectedItem.current) {
                        selectItem(focusedItem.current);
                        focusedItem.current.click();
                    }
                    closeDropdown();
                }
                break;
            }
            case 'ArrowUp':
            case 'ArrowDown': {
                hijackEvent();
                if (!dropdownOpen.current) {
                    openDropdown();
                    break;
                }

                const { previousSibling, nextSibling } = focusedItem.current ?? {};
                const sibling = e.key === 'ArrowUp' ? previousSibling : nextSibling;
                if (!sibling) return;
                focusItem(sibling as HTMLButtonElement);
                break;
            }
            case 'Escape': {
                hijackEvent();
                closeDropdown();
                break;
            }
            case 'Tab': {
                updateItem('select');
                if (dropdownOpen.current) closeDropdown();
                break;
            }
            default: {
                if (timeout) clearTimeout(timeout);
                // @ts-ignore
                timeout = setTimeout(() => updateItem('focus'), 250);
                break;
            }
        }
    }

    function toggleDropdown(state: boolean) {
        const action = state ? 'add' : 'remove';
        dropdown.current!.classList[action](SELECT_DROPDOWN_ACTIVE);
        element.classList[action](FLOATING_LABEL);
    }

    function selectItem(item: HTMLButtonElement) {
        const { value } = item.dataset;

        // Remove active class from current element.
        if (selectedItem.current) {
            selectedItem.current.classList.remove(SELECT_ITEM_ACTIVE);
        }

        item.classList.add(SELECT_ITEM_ACTIVE);

        // Set the value of the selectbox
        selectInput.current!.rebindValue(`${value}`);
        selectElement.current!.dispatchEvent(new Event('change'));

        selectedItem.current = item;

        if (!disableEvents) {
            // Manually trigger form change event
            form?.dispatchEvent(new Event('change'));
        }
    }

    function focusItem(item?: HTMLButtonElement | null) {
        [...selectItems.current].forEach((item) => item?.classList.remove(SELECT_ITEM_FOCUS));

        if (item) {
            focusedItem.current = item;
            focusedItem.current.classList.add(SELECT_ITEM_FOCUS);

            const index = [...selectItems.current].indexOf(item);
            dropdown.current?.scrollTo({ top: index * DROPDOWN_SCROLL, behavior: 'smooth' });
        }
    }
};

Select.props = {
    mobileEnabled: {
        type: Boolean,
        reflectToAttr: true,
    },
    disableEvents: {
        type: Boolean,
        reflectToAttr: true,
    },
    preselected: {
        type: Boolean,
        reflectToAttr: true,
    },
};

defineElement('bpd-select', Select);
